import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import Text, {
  TextColors,
  TextPresets,
} from '@stripcash/ui-kit/components/Text';
import toArray from '@stripcash/ui-kit/utils/toArray';
import usePortal from 'react-cool-portal';

import { ALLOWED_LANGUAGES } from 'common/i18n/constants';
import loadLanguage from 'common/i18n/loadLanguage';
import { useLocale } from 'common/i18n';
import { UserSettings } from 'common/services/userSettings/types';
import setUserSettingsService from 'common/services/userSettings/setUserSettingsService';

import HeaderButton from '../HeaderButton';

import s from './styles.module.css';
import {
  LANGUAGE_POPUP_ID,
  DEFAULT_LANGUAGE,
  ICONS_BY_LANGUAGE,
} from './constants';

type LanguagesType = {
  code: string;
  name: string;
}[];

const LanguagePopup: FC = () => {
  const { locale, updateLocale } = useLocale();
  const [languages, setLanguages] = useState<LanguagesType>([]);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);

  const { Portal, show, hide, isShow } = usePortal({
    containerId: LANGUAGE_POPUP_ID,
    escToHide: false,
    clickOutsideToHide: true,
    defaultShow: false,
  });

  // to drop current language on the bottom
  const sortedLanguages = useMemo(
    () => [
      ...languages.filter(({ code }) => code !== currentLanguage.code),
      currentLanguage,
    ],
    [currentLanguage, languages],
  );

  const handleChangeLanguage = useCallback(
    ({ code, name }) => {
      setUserSettingsService({ name: UserSettings.Languages, value: code });
      updateLocale(code);
      setCurrentLanguage({ code, name });
      hide();
    },
    [updateLocale, hide],
  );

  useEffect(() => {
    if (locale) {
      setCurrentLanguage(
        languages.find(({ code }) => code === locale) || DEFAULT_LANGUAGE,
      );
    }
  }, [languages, locale]);

  useEffect(() => {
    Promise.all(
      toArray(ALLOWED_LANGUAGES).map((lang) =>
        loadLanguage(lang, 'sidebarLanguage'),
      ),
    ).then((fullLanguageNames) =>
      setLanguages(
        fullLanguageNames.map(({ currentLanguage: language }, index) => ({
          code: ALLOWED_LANGUAGES[index],
          name: language,
        })),
      ),
    );
  }, []);

  const handleHeaderButtonClick = useCallback(() => {
    if (isShow) {
      hide();
    } else {
      show();
    }
  }, [hide, isShow, show]);

  return (
    <>
      <HeaderButton
        onClick={handleHeaderButtonClick}
        title={currentLanguage.name}
        icon={ICONS_BY_LANGUAGE[currentLanguage.code]}
      />
      <Portal>
        <div className={s.languagePopover}>
          {sortedLanguages.map(({ code, name }) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              key={code}
              onClick={() => handleChangeLanguage({ code, name })}
              className={cn(s.language, {
                [s.active]: currentLanguage.code === code,
              })}
            >
              {ICONS_BY_LANGUAGE[code]}
              <Text preset={TextPresets.BodyBold14} color={TextColors.White}>
                {name}
              </Text>
            </div>
          ))}
        </div>
      </Portal>
    </>
  );
};

export default LanguagePopup;
